import { useEffect, useState } from 'react';
import {
  PAYMENT_STATUS_LABELS, PAYMENT_CHANNEL_LABELS, SETTLED_VIA_LABELS, KNOWN_BANK_CODES,
  formatRupiah,
  type PaymentStatus, type PaymentView, type PaymentChannelType, type HospitalDebtView,
} from 'shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';

interface PaymentRow {
  id: string; paymentNumber: string; status: PaymentStatus;
  amount: string; finalAmount: string | null; bloodAmount: string;
  orderFeeAmount: string; paymentFeeAmount: string;
  methodType: PaymentChannelType | null;
  paidAt: string | null; expiresAt: string | null; createdAt: string;
  hospitalName: string; invoiceUrl: string | null; gatewayError: string | null;
}

interface PayableRow {
  orderId: string; orderNumber: string; status: string;
  bagCount: number; bloodAmount: string; orderFeeAmount: string;
  allocatedAt: string | null;
}

const fmtDateTime = (iso: string | null) => iso
  ? new Date(iso).toLocaleString('id-ID', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
  : '—';

/** How long is left before an invoice lapses, phrased the way a person would say it. */
function timeLeft(iso: string | null): string | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'sudah lewat';
  const hours = Math.floor(ms / 3_600_000);
  const minutes = Math.floor((ms % 3_600_000) / 60_000);
  return hours > 0 ? `${hours} jam ${minutes} menit lagi` : `${minutes} menit lagi`;
}

export function Payments() {
  const { can } = useAuth();
  const canPay = can('payment:create');

  const [rows, setRows] = useState<PaymentRow[]>([]);
  const [payable, setPayable] = useState<PayableRow[]>([]);
  const [debts, setDebts] = useState<HospitalDebtView[]>([]);
  const [fees, setFees] = useState<{ onOrder: string; onPayment: string } | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<PaymentView | null>(null);
  const [manualNote, setManualNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  async function loadAll() {
    setLoading(true);
    try {
      const [list, pay, debt, fee] = await Promise.all([
        api.get<PaymentRow[]>('/payments'),
        canPay ? api.get<PayableRow[]>('/payments/payable').catch(() => []) : Promise.resolve([]),
        can('debt:read') ? api.get<HospitalDebtView[]>('/payments/debt').catch(() => []) : Promise.resolve([]),
        api.get<{ onOrder: string; onPayment: string }>('/ledger/fees/current').catch(() => null),
      ]);
      setRows(list);
      setPayable(pay);
      setDebts(debt);
      setFees(fee);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Gagal memuat data pembayaran.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void loadAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  function toggle(orderId: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId); else next.add(orderId);
      return next;
    });
  }

  // The running total, so a finance user sees what the invoice will say BEFORE creating it.
  const chosen = payable.filter((o) => picked.has(o.orderId));
  const bloodTotal = chosen.reduce((sum, o) => sum + BigInt(o.bloodAmount), 0n);
  const orderFeeTotal = chosen.reduce((sum, o) => sum + BigInt(o.orderFeeAmount), 0n);
  const paymentFee = BigInt(fees?.onPayment ?? '0');
  const grandTotal = chosen.length > 0 ? bloodTotal + orderFeeTotal + paymentFee : 0n;

  async function act(fn: () => Promise<unknown>, ok: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(ok);
      await loadAll();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Tindakan gagal.');
    } finally {
      setBusy(false);
    }
  }

  async function createPayment() {
    await act(async () => {
      const created = await api.post<PaymentView>('/payments', { orderIds: [...picked] });
      setPicked(new Set());
      setSelected(created);
    }, 'Tagihan dibuat. Pilih metode pembayaran untuk mendapatkan nomor virtual account atau QRIS.');
  }

  async function open(id: string) {
    setSelected(await api.get<PaymentView>(`/payments/${id}`));
  }

  async function chooseMethod(type: PaymentChannelType, bankCode: string) {
    if (!selected) return;
    await act(async () => {
      setSelected(await api.post<PaymentView>(`/payments/${selected.id}/method`, { type, bankCode }));
    }, 'Metode pembayaran dilampirkan.');
  }

  async function sync(id: string) {
    await act(async () => {
      const result = await api.post<{ message: string }>(`/payments/${id}/sync`, {});
      setNotice(result.message);
      setSelected(await api.get<PaymentView>(`/payments/${id}`));
    }, 'Status diperiksa.');
  }

  return (
    <div className="stack">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h1>Pembayaran</h1>
        {fees && (
          <p className="muted">
            Biaya transaksi saat ini: {formatRupiah(fees.onOrder)} per pemesanan,{' '}
            {formatRupiah(fees.onPayment)} per pembayaran.
          </p>
        )}
      </div>

      {error && <div className="msg error">{error}</div>}
      {notice && <div className="msg ok">{notice}</div>}

      {debts.length > 0 && (
        <div className="card stack">
          <h2>Tagihan terutang</h2>
          <table>
            <thead>
              <tr>
                <th>Rumah Sakit</th>
                <th style={{ textAlign: 'right' }}>Bisa dibayar</th>
                <th style={{ textAlign: 'right' }}>Sedang diproses</th>
                <th style={{ textAlign: 'right' }}>Pesanan</th>
              </tr>
            </thead>
            <tbody>
              {debts.map((d) => (
                <tr key={d.hospitalId}>
                  <td>{d.hospitalName}</td>
                  <td className="mono" style={{ textAlign: 'right' }}>{formatRupiah(d.payableAmount)}</td>
                  <td className="mono muted" style={{ textAlign: 'right' }}>{formatRupiah(d.inProgressAmount)}</td>
                  <td style={{ textAlign: 'right' }}>{d.orderCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted">
            &ldquo;Sedang diproses&rdquo; sudah masuk tagihan yang belum lunas, jadi tidak bisa
            dipilih lagi sampai tagihannya kedaluwarsa atau dibatalkan.
          </p>
        </div>
      )}

      {canPay && (
        <div className="card stack">
          <h2>Buat tagihan baru</h2>
          {payable.length === 0 ? (
            <p className="muted">
              Tidak ada pesanan yang siap dibayar. Pesanan baru muncul di sini setelah PMI
              mengalokasikan kantong darahnya.
            </p>
          ) : (
            <>
              <table>
                <thead>
                  <tr>
                    <th style={{ width: '2.5rem' }} />
                    <th>Pesanan</th>
                    <th style={{ textAlign: 'right' }}>Kantong</th>
                    <th style={{ textAlign: 'right' }}>Darah</th>
                    <th style={{ textAlign: 'right' }}>Biaya pemesanan</th>
                    <th>Dialokasikan</th>
                  </tr>
                </thead>
                <tbody>
                  {payable.map((o) => (
                    <tr key={o.orderId}>
                      <td>
                        <input
                          type="checkbox"
                          checked={picked.has(o.orderId)}
                          onChange={() => toggle(o.orderId)}
                          aria-label={`Pilih pesanan ${o.orderNumber}`}
                        />
                      </td>
                      <td className="mono">{o.orderNumber}</td>
                      <td style={{ textAlign: 'right' }}>{o.bagCount}</td>
                      <td className="mono" style={{ textAlign: 'right' }}>{formatRupiah(o.bloodAmount)}</td>
                      <td className="mono muted" style={{ textAlign: 'right' }}>{formatRupiah(o.orderFeeAmount)}</td>
                      <td className="muted">{fmtDateTime(o.allocatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="stack" style={{ gap: '0.25rem' }}>
                  <span className="muted">
                    {chosen.length} pesanan dipilih · darah {formatRupiah(bloodTotal.toString())}
                    {orderFeeTotal > 0n && <> · biaya pemesanan {formatRupiah(orderFeeTotal.toString())}</>}
                    {chosen.length > 0 && paymentFee > 0n && <> · biaya pembayaran {formatRupiah(paymentFee.toString())}</>}
                  </span>
                  <strong className="mono">Total {formatRupiah(grandTotal.toString())}</strong>
                </div>
                <button
                  className="primary"
                  disabled={busy || picked.size === 0}
                  onClick={() => void createPayment()}
                >
                  Buat tagihan
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <div className="card stack">
        <h2>Riwayat pembayaran</h2>
        {loading ? <p className="muted">Memuat…</p> : rows.length === 0 ? (
          <p className="muted">Belum ada pembayaran.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Nomor</th>
                <th>Rumah Sakit</th>
                <th>Status</th>
                <th>Metode</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th>Dibuat</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="mono">{r.paymentNumber}</td>
                  <td>{r.hospitalName}</td>
                  <td>
                    <span className={`pill${r.status === 'pending' ? ' accent' : ''}`}>
                      {PAYMENT_STATUS_LABELS[r.status]}
                    </span>
                  </td>
                  <td className="muted">{r.methodType ? PAYMENT_CHANNEL_LABELS[r.methodType] : '—'}</td>
                  <td className="mono" style={{ textAlign: 'right' }}>
                    {formatRupiah(r.finalAmount ?? r.amount)}
                  </td>
                  <td className="muted">{fmtDateTime(r.createdAt)}</td>
                  <td>
                    <button onClick={() => void open(r.id)}>Rincian</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <div className="card stack">
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
            <h2 className="mono">{selected.paymentNumber}</h2>
            <button onClick={() => setSelected(null)}>Tutup</button>
          </div>

          <div className="grid-2">
            <div className="stack" style={{ gap: '0.25rem' }}>
              <span className="label">Status</span>
              <span className="pill">{PAYMENT_STATUS_LABELS[selected.status]}</span>
              {selected.settledVia && (
                <span className="muted">Dilunasi: {SETTLED_VIA_LABELS[selected.settledVia]}
                  {selected.settledByName ? ` — ${selected.settledByName}` : ''}</span>
              )}
              {selected.status === 'pending' && selected.expiresAt && (
                <span className="muted">Berlaku {timeLeft(selected.expiresAt)}</span>
              )}
            </div>

            <div className="stack" style={{ gap: '0.25rem' }}>
              <span className="label">Rincian</span>
              <span className="mono">Darah {formatRupiah(selected.bloodAmount)}</span>
              <span className="mono muted">Biaya pemesanan {formatRupiah(selected.orderFeeAmount)}</span>
              <span className="mono muted">Biaya pembayaran {formatRupiah(selected.paymentFeeAmount)}</span>
              <strong className="mono">Total tagihan {formatRupiah(selected.amount)}</strong>
              {selected.adminAmount && BigInt(selected.adminAmount) > 0n && (
                <span className="mono muted">
                  Biaya admin bank {formatRupiah(selected.adminAmount)} — dibayar ke gateway,
                  bukan ke PMI
                </span>
              )}
              {selected.finalAmount && (
                <strong className="mono">Dibayar {formatRupiah(selected.finalAmount)}</strong>
              )}
            </div>
          </div>

          {selected.gatewayError && (
            <div className="msg error">
              AiYO menolak pembuatan invoice: {selected.gatewayError}. Pesanan sudah dilepas dan
              bisa dimasukkan ke tagihan baru.
            </div>
          )}

          <div className="stack" style={{ gap: '0.25rem' }}>
            <span className="label">Pesanan dalam tagihan ini</span>
            {selected.orders.map((o) => (
              <span key={o.orderId} className="mono">
                {o.orderNumber} — {formatRupiah(o.bloodAmount)}
                {BigInt(o.orderFeeAmount) > 0n && <span className="muted"> + biaya {formatRupiah(o.orderFeeAmount)}</span>}
              </span>
            ))}
          </div>

          {selected.status === 'pending' && canPay && !selected.accountNumber && !selected.qrisData && (
            <div className="stack">
              <span className="label">Pilih metode pembayaran</span>
              <div className="row" style={{ flexWrap: 'wrap' }}>
                {KNOWN_BANK_CODES.map((b) => (
                  <button
                    key={b.code}
                    disabled={busy}
                    onClick={() => void chooseMethod(b.type, b.code)}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
              <p className="muted">
                Invoice dibuat tanpa metode, lalu metodenya dilampirkan — jadi metode masih
                bisa diganti tanpa membuat tagihan baru.
              </p>
            </div>
          )}

          {selected.accountNumber && (
            <div className="stack" style={{ gap: '0.25rem' }}>
              <span className="label">
                {selected.methodType ? PAYMENT_CHANNEL_LABELS[selected.methodType] : 'Rekening'}
              </span>
              <strong className="mono" style={{ fontSize: '1.25rem' }}>{selected.accountNumber}</strong>
              {selected.accountName && <span className="muted">{selected.accountName}</span>}
              {selected.methodExpiresAt && (
                <span className="muted">Berlaku sampai {fmtDateTime(selected.methodExpiresAt)}</span>
              )}
            </div>
          )}

          {selected.qrisData && (
            <div className="stack" style={{ gap: '0.25rem' }}>
              <span className="label">QRIS</span>
              <code className="mono" style={{ wordBreak: 'break-all', fontSize: '0.75rem' }}>
                {selected.qrisData}
              </code>
              <span className="muted">Render payload ini menjadi QR di aplikasi pembayaran.</span>
            </div>
          )}

          <div className="row" style={{ flexWrap: 'wrap' }}>
            {selected.invoiceUrl && (
              <a className="pill" href={selected.invoiceUrl} target="_blank" rel="noreferrer">
                Buka invoice AiYO
              </a>
            )}
            {selected.status === 'pending' && (
              <button disabled={busy} onClick={() => void sync(selected.id)}>Cek status</button>
            )}
          </div>

          {can('payment:settle_manual') && selected.status !== 'paid' && (
            <div className="stack">
              <span className="label">Tandai lunas manual</span>
              <p className="muted">
                Untuk uang yang masuk lewat transfer langsung, di luar gateway. Tercatat
                sebagai pelunasan manual beserta nama Anda, dan langsung membentuk baris
                buku besar.
              </p>
              <div className="field">
                <input
                  value={manualNote}
                  onChange={(e) => setManualNote(e.target.value)}
                  placeholder="Dasar pelunasan, misalnya nomor referensi transfer"
                />
              </div>
              <button
                className="danger"
                disabled={busy || manualNote.trim().length < 3}
                onClick={() => void act(async () => {
                  setSelected(await api.post<PaymentView>(
                    `/payments/${selected.id}/settle-manual`, { note: manualNote },
                  ));
                  setManualNote('');
                }, 'Pembayaran ditandai lunas.')}
              >
                Tandai lunas
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
